// Phase 9B — BoardsPage.
//
// Multi-board operating system dashboard. Sidebar navigation for
// 6 board types + activity feed + approval queue. Desktop-first,
// with premium Bebas Neue / JetBrains Mono / DM Sans typography.

import { useState } from "react";
import type { CSSProperties } from "react";
import type { BoardType } from "../../boards/useBoardItems.ts";
import { useBoardItems, useActivityLog, usePendingApprovals } from "../../boards/useBoardItems.ts";
import { BoardView } from "../../modes/boards/BoardView.tsx";
import { BoardDashboardOverview } from "../../modes/boards/BoardDashboardOverview.tsx";
import { ActivityFeed } from "../../modes/boards/ActivityFeed.tsx";
import { ApprovalQueue } from "../../modes/boards/ApprovalQueue.tsx";
import { cardLow } from "../../styles/elevation.ts";
import { transitionFast } from "../../styles/motion.ts";

// ─── Nav types ─────────────────────────────────────────────────

type NavKey = BoardType | "overview" | "activity" | "approvals";

interface NavItem {
  key: NavKey;
  label: string;
  shortLabel: string;
  icon: string; // Mono character icon
}

const NAV_ITEMS: NavItem[] = [
  { key: "overview", label: "Dashboard Overview", shortLabel: "Overview", icon: "⊞" },
  { key: "opportunities", label: "Market Opportunities", shortLabel: "Opps", icon: "◆" },
  { key: "chains", label: "ROLLOVER Chains", shortLabel: "Chains", icon: "⟐" },
  { key: "risk", label: "Risk Board", shortLabel: "Risk", icon: "▲" },
  { key: "results", label: "Results & Resolution", shortLabel: "Results", icon: "◈" },
  { key: "goals", label: "User Goals", shortLabel: "Goals", icon: "◉" },
  { key: "admin", label: "Admin Operations", shortLabel: "Admin", icon: "⚙" },
  { key: "activity", label: "Activity Log", shortLabel: "Activity", icon: "▸" },
  { key: "approvals", label: "Approval Queue", shortLabel: "Approvals", icon: "✓" },
];

// ─── Styles ────────────────────────────────────────────────────

const pageRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

const pageHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

const pageTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

const pageSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: "4px",
};

const layoutGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "220px 1fr",
  gap: "var(--sz-space-4)",
  alignItems: "start",
};

const navSidebar: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  position: "sticky",
  top: "var(--sz-space-4)",
};

const navSection: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  padding: "var(--sz-space-2) var(--sz-space-2) var(--sz-space-1)",
};

const navItem: CSSProperties = {
  ...transitionFast,
  display: "flex",
  alignItems: "center",
  gap: "var(--sz-space-2)",
  padding: "8px var(--sz-space-2)",
  borderRadius: "var(--sz-radius-sm)",
  borderTop: "none",
  borderRight: "none",
  borderBottom: "none",
  borderLeft: "2px solid transparent",
  background: "transparent",
  cursor: "pointer",
  width: "100%",
  textAlign: "left",
};

const navItemDefault: CSSProperties = {
  ...navItem,
  color: "var(--sz-text-secondary)",
};

const navItemActive: CSSProperties = {
  ...navItem,
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-primary)",
  borderLeft: "2px solid var(--sz-amber)",
};

const navIcon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  width: "20px",
  textAlign: "center",
  flexShrink: 0,
};

const navLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "0.5px",
};

const contentArea: CSSProperties = {
  minHeight: "400px",
};

// ─── Board panel renderer ──────────────────────────────────────

function BoardPanel({ boardType }: { boardType: BoardType }): JSX.Element {
  const state = useBoardItems(boardType);
  return <BoardView boardType={boardType} state={state} />;
}

function ActivityPanel(): JSX.Element {
  const state = useActivityLog(undefined, 100);
  return <ActivityFeed state={state} />;
}

function ApprovalPanel(): JSX.Element {
  const state = usePendingApprovals();
  return <ApprovalQueue state={state} />;
}

// ─── Page ──────────────────────────────────────────────────────

export function BoardsPage(): JSX.Element {
  const [activeNav, setActiveNav] = useState<NavKey>("overview");

  return (
    <div style={pageRoot}>
      {/* Header */}
      <div style={pageHeader}>
        <div>
          <h1 style={pageTitle}>The Board</h1>
          <div style={pageSubtitle}>Operating system · 6 boards · all markets</div>
        </div>
      </div>

      {/* Layout */}
      <div style={layoutGrid}>
        {/* Sidebar nav */}
        <nav style={navSidebar}>
          <span style={navSection}>Boards</span>
          {NAV_ITEMS.slice(0, 7).map((item) => (
            <button
              key={item.key}
              type="button"
              style={activeNav === item.key ? navItemActive : navItemDefault}
              onClick={() => setActiveNav(item.key)}
            >
              <span style={navIcon}>{item.icon}</span>
              <span style={navLabel}>{item.shortLabel}</span>
            </button>
          ))}

          <span style={{ ...navSection, marginTop: "var(--sz-space-2)" }}>System</span>
          {NAV_ITEMS.slice(7).map((item) => (
            <button
              key={item.key}
              type="button"
              style={activeNav === item.key ? navItemActive : navItemDefault}
              onClick={() => setActiveNav(item.key)}
            >
              <span style={navIcon}>{item.icon}</span>
              <span style={navLabel}>{item.shortLabel}</span>
            </button>
          ))}
        </nav>

        {/* Content */}
        <div style={contentArea}>
          {activeNav === "overview" ? (
            <BoardDashboardOverview onNavigateBoard={(bt) => setActiveNav(bt)} />
          ) : activeNav === "activity" ? (
            <ActivityPanel />
          ) : activeNav === "approvals" ? (
            <ApprovalPanel />
          ) : (
            <BoardPanel boardType={activeNav as BoardType} />
          )}
        </div>
      </div>
    </div>
  );
}
